
import { defineComponent, ref, watch, getCurrentInstance } from 'vue'
import { useEventListener } from '@vueuse/core'
import { FnKeyType } from '@/components/FunctionKey/types'

export default defineComponent({
  props: {
    functionKeys: {
      type: Array as () => FnKeyType[],
      required: true,
      default: () => []
    },
    justify: {
      type: String,
      required: false,
      default: 'start'
    },
    disabled: {
      // when dialog shows, should disable outer function keys. If not, action will be doubled.
      required: false,
      type: Boolean,
      default: false
    },
    btnLarge: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const root = getCurrentInstance()?.proxy?.$root as
      | { isLoading?: boolean }
      | undefined
    const containerRef = ref<HTMLElement | null>(null)
    const cleanup = ref<any>(null)
    const isOnRunning = ref(false)
    const onRunningKey = ref<any>(undefined)
    const runListener = (funcKey: any) => {
      if (isOnRunning.value && onRunningKey.value === funcKey.key) return
      isOnRunning.value = true
      onRunningKey.value = funcKey.key
      funcKey.func()
      setTimeout(() => {
        isOnRunning.value = false
        onRunningKey.value = undefined
      }, 300)
    }

    const findFuncKey = (event: KeyboardEvent): FnKeyType | undefined => {
      const key = event.ctrlKey ? `CTRL${event.key}` : event.key
      return props.functionKeys.find(
        (fnKey: FnKeyType) => key.toUpperCase() === fnKey.key.toUpperCase()
      )
    }
    // Walk up the DOM to find the nearest ancestor with an explicit z-index.
    // This reflects the stacking context this component lives in (e.g. inside a dialog).
    const getEffectiveZIndex = (el: HTMLElement | null): number => {
      let current: HTMLElement | null = el
      while (current && current !== document.body) {
        const zIndex = window.getComputedStyle(current).zIndex
        const parsed = parseInt(zIndex, 10)
        if (!isNaN(parsed)) return parsed
        current = current.parentElement
      }
      return 0
    }

    const pressKeyDown = async (event: KeyboardEvent) => {
      // Check if api is calling by checking loading element existed
      const cycleLoader = document.querySelector(
        '.progress_circle_loader-div .v-progress-circular'
      )
      const blurLoader = document.querySelector('.blur-progress-loader-div')
      if (cycleLoader && blurLoader) {
        return // Should not execute keydown logic when api is loading
      }
      // Same guard for pages using the global $root.isLoading overlay (AppLoading.vue)
      // instead of the page-local blur/circle loader above.
      if (root?.isLoading) {
        return
      }

      const funcKey = findFuncKey(event)

      // Block key handling if this component is visually behind an active dialog overlay.
      // Compares this component's stacking z-index against each overlay's z-index so that
      // function keys inside the topmost overlay remain active while all overlays underneath
      // (background bars, or a bar's own dialog when a nested confirm dialog opens on top of it)
      // are suppressed. Only relevant when the pressed key actually matches one of this bar's own
      // function keys - otherwise we'd end up preventDefault-ing unrelated keys (e.g. Enter on a
      // focused dialog button).
      const activeOverlays = document.querySelectorAll('.v-overlay--active')
      if (activeOverlays.length > 0 && funcKey) {
        const myZIndex = getEffectiveZIndex(containerRef.value)
        const isCovered = Array.from(activeOverlays).some((overlay) => {
          const zIndexVal = window.getComputedStyle(
            overlay as HTMLElement
          ).zIndex
          const parsed = parseInt(zIndexVal, 10)
          const z = isNaN(parsed) ? 0 : parsed
          return z > myZIndex
        })
        if (isCovered) {
          event.preventDefault()
          return
        }
      }
      if (!funcKey) return
      // If function key is disabled, should not handle function key event
      if (funcKey.disabled) return
      event.preventDefault()
      runListener(funcKey)
    }

    cleanup.value = useEventListener(document, 'keydown', pressKeyDown)
    const onClickKey = (fn: any) => {
      runListener(fn)
    }
    watch(
      () => props.disabled,
      (newVal) => {
        if (newVal) cleanup.value()
        else {
          cleanup.value = useEventListener(document, 'keydown', pressKeyDown)
        }
      }
    )

    return {
      containerRef,
      isOnRunning,
      onRunningKey,
      onClickKey
    }
  }
})
